import linkifyHtml from 'linkify-html';
import type { Opts } from 'linkifyjs';
import type { App, DirectiveBinding, ObjectDirective } from 'vue';
import xss, { getDefaultWhiteList } from 'xss';

import { matchers } from './matchers';
import type { Matcher, MatcherList } from './types';

const defaultXssAllowList = getDefaultWhiteList();
const linkifyXssAllowList = {
  ...defaultXssAllowList,
  a: [
    ...(defaultXssAllowList.a ?? []),
    'class',
    'data-latitude',
    'data-longitude',
  ],
};

class LinkifyDirectiveService {
  private substitutionCounter = 0;

  constructor(
    private readonly defaultOptions: Opts,
    private readonly matcherList: MatcherList,
  ) {
  }

  linkify(rawHtml: string, options?: Opts): string {
    const sanitizedHtml = xss(rawHtml, {
      allowList: linkifyXssAllowList,
    });
    const htmlWithMatchers = this.replaceMatchers(sanitizedHtml);

    return linkifyHtml(htmlWithMatchers, options);
  }

  createDirective(): ObjectDirective<HTMLElement, Opts | undefined> {
    return {
      mounted: (
        element: HTMLElement,
        binding: DirectiveBinding<Opts | undefined>,
      ) => {
        this.tryLinkify(element, binding.value ?? this.defaultOptions);
      },
      updated: (
        element: HTMLElement,
        binding: DirectiveBinding<Opts | undefined>,
      ) => {
        this.tryLinkify(element, binding.value ?? this.defaultOptions);
      },
    };
  }

  private tryLinkify(element: HTMLElement, options: Opts): void {
    try {
      element.innerHTML = this.linkify(element.innerHTML, options);
    }
    catch {
      console.error('Failed to apply linkify directive.');
    }
  }

  private createPlaceholderKey(): string {
    return `__sub${this.substitutionCounter++}__`;
  }

  private applyMatcher(text: string, matcher: Matcher, mutablePlaceholderMap: Map<string, string>): string {
    return text.replace(
      matcher.regex,
      (...matchArguments: string[]): string => {
        const substitution = matcher.matchCallback(...matchArguments);

        const substitutionKey = this.createPlaceholderKey();
        mutablePlaceholderMap.set(substitutionKey, substitution);
        return substitutionKey;
      },
    );
  }

  private replaceMatchers(text: string): string {
    let textWithPlaceholderKeys = text;
    const placeholderSubstitutionMap = new Map<string, string>();

    for (const matcher of Object.values(this.matcherList)) {
      textWithPlaceholderKeys = this.applyMatcher(textWithPlaceholderKeys, matcher, placeholderSubstitutionMap);
    }

    if (!placeholderSubstitutionMap.size) {
      return textWithPlaceholderKeys;
    }

    return textWithPlaceholderKeys.replace(
      /__sub\d+__/g,
      (placeholderKey: string): string => placeholderSubstitutionMap.get(placeholderKey) ?? placeholderKey,
    );
  }
}

export function installLinkifyDirective(app: App, options: Opts = {}): void {
  const linkifyDirectiveService = new LinkifyDirectiveService(options, matchers);
  app.directive('linkify', linkifyDirectiveService.createDirective());
}
